/**
 * `<GmEncounters>` — the GM's **ENCOUNTERS** page: the table's scenes (`decisions/0203` point 7).
 * A *Narrative* row, then every encounter on the world server (`GET /api/world/encounters`), each
 * tagged ON TABLE (what every client's stage shows) or PEEKING (this GM's private preview), with:
 *
 *  - **Peek** — show the encounter on this GM's stage only; players see nothing change. The stage
 *    marks the preview with a hazard-striped bar (RETURN / PUT ON TABLE).
 *  - **Put on table** — make the scene the table's (`setTableScene`), ending any preview.
 *  - **✕** — delete the encounter (GM-authed), then refresh.
 *
 * An ended fight whose wear is not on the roster yet carries a "wear not committed" line
 * (`wearUncommitted`); the Director's SCENE section commits it. The list re-reads on every table
 * scene change, since ending a fight is one.
 *
 * The ＋ NEW ENCOUNTER button opens the {@link NewEncounterForm}. The host key (held on the session)
 * gates create/delete; listing works without it. `refreshKey` forces a re-fetch (a new encounter).
 */
import { faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useCallback, useEffect, useState } from "react";
import { GRID_MODES, type EncounterSummary } from "@lancer-vtt/shared";
import { deleteEncounter, listEncounters } from "../../lib/api";
import type { RosterState } from "@lancer-vtt/domain";
import { useGameSessionContext } from "../../lib/game/GameSessionProvider";
import { wearUncommitted } from "../../lib/game/sceneStatus";
import { Icon } from "../../lib/Icon";
import { tipAttrs } from "../../lib/tooltip";
import { useStage } from "./shell/stageContext";
import { sceneRows } from "./shell/stageScene";

/** The grid mode's display label (falls back to the raw value for an unknown mode). */
const GRID_LABEL = new Map(GRID_MODES.map((g) => [g.value, g.label]));

export function GmEncounters({
  hostKey,
  refreshKey,
  onNew,
  roster,
}: {
  /** The GM host key — required to create/delete; null disables those affordances. */
  hostKey: string | null;
  /** Bump to force a re-fetch (e.g. a new encounter was minted). */
  refreshKey: number;
  /** Open the create-new-encounter form. */
  onNew: () => void;
  /** The roster, for which ended fights still owe their wear; null while it loads. */
  roster: RosterState | null;
}) {
  const session = useGameSessionContext();
  const stage = useStage();
  const [encounters, setEncounters] = useState<EncounterSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const listed = await listEncounters();
    if (listed.ok) setEncounters(listed.value);
    else setError(listed.error);
  }, []);

  const sceneKey = session.tableScene.kind === "encounter" ? session.tableScene.encounterId : "narrative";
  useEffect(() => {
    void reload();
  }, [reload, refreshKey, sceneKey]);

  const remove = async (enc: EncounterSummary) => {
    if (!hostKey || busy) return;
    if (!confirm(`Delete encounter "${enc.name}"? This can't be undone.`)) return;
    setBusy(true);
    setError(null);
    const removed = await deleteEncounter(enc.id, hostKey);
    if (removed.ok) await reload();
    else setError(removed.error);
    setBusy(false);
  };

  const byId = new Map((encounters ?? []).map((e) => [e.id, e]));
  const rows = sceneRows({ table: session.tableScene, preview: stage?.preview ?? null, encounters: encounters ?? [] });

  return (
    <div className="gms-scenes">
      {error && <div className="gms-scenes-err">{error}</div>}
      <div className="gms-scenes-label">Scenes</div>
      <ul className="gms-scenes-list" aria-label="Scenes">
        {rows.map((row) => {
          const enc = row.scene.kind === "encounter" ? byId.get(row.scene.encounterId) : undefined;
          const name = row.scene.kind === "narrative" ? "Narrative" : enc?.name || "Unnamed encounter";
          const sub = row.scene.kind === "narrative" ? "Theatre of the mind" : enc ? (GRID_LABEL.get(enc.gridMode) ?? enc.gridMode) : "";
          const key = row.scene.kind === "narrative" ? "narrative" : row.scene.encounterId;
          return (
            <li key={key} className="gms-scene" data-scene={key}>
              <span className="gms-scene-text">
                <span className="gms-scene-name">{name}</span>
                <span className="gms-scene-sub">{sub}</span>
                {enc && wearUncommitted({ encounter: enc, roster }) && <span className="gms-scene-owed">Over · wear not committed</span>}
              </span>
              {row.onTable && <span className="gms-scene-tag">ON TABLE</span>}
              {row.previewing && <span className="gms-scene-tag gms-scene-tag--view">PEEKING</span>}
              {row.canPeek && row.scene.kind === "encounter" && (
                <button type="button" className="gms-scene-btn" aria-label={`Peek ${name}`} onClick={() => stage?.peek(key)}>
                  Peek
                </button>
              )}
              {row.canPut && (
                <button type="button" className="gms-scene-btn" aria-label={`Put ${name} on table`} onClick={() => stage?.putOnTable(row.scene)}>
                  Put on table
                </button>
              )}
              {enc && (
                <button
                  type="button"
                  className="gms-scene-del"
                  aria-label={`Delete ${enc.name}`}
                  {...tipAttrs({ label: `Delete ${enc.name}` })}
                  disabled={!hostKey || busy}
                  onClick={() => remove(enc)}
                >
                  <Icon icon={faXmark} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <p className="gms-scenes-note">Peek shows a scene on your stage only. Put on table puts it on every stage.</p>
      <button type="button" className="gms-scenes-new" title="Create a new encounter" disabled={!hostKey || busy} onClick={onNew}>
        <Icon icon={faPlus} className="icon-lead" />
        NEW ENCOUNTER
      </button>
    </div>
  );
}
